import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { cn } from "@arcadeai/design-system/lib/utils";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Book, CreditCard, Key } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, Suspense } from "react";
import { hasOrgPermission } from "@/app/capabilities/api/check-permission";
import { orgCapabilitiesQueryOptions } from "@/app/capabilities/api/get-capabilities";
import { useSuspenseCapability } from "@/app/capabilities/api/hooks";
import { CanProject } from "@/app/capabilities/components/can-project";
import { ORG, PROJECT } from "@/app/capabilities/permissions";
import { eligibilityQueryOptions } from "@/app/onboarding/api/get-eligibility";
import { onboardingProgressQueryOptions } from "@/app/onboarding/api/get-onboarding-progress";
import { shouldShowSidebarCta } from "@/app/onboarding/lib/selectors";
import { OnboardingCtaButton } from "@/app/onboarding-first-win/components/onboarding-cta-button";
import { NavMenu } from "@/components/custom/sidebar/nav-menu";
import { NavUser } from "@/components/custom/sidebar/nav-user";
import { UpgradeButton } from "@/components/custom/sidebar/upgrade-card";
import { buildOrgProjectPath } from "@/lib/org-project-context/constants";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { data } from "./data";
import { NavMenuItem } from "./nav-menu-item";
import { DashboardSidebarHeader } from "./sidebar-header";

const DashboardMenu = () => (
  <motion.div className="mt-2" key="nav-main">
    <NavMenu items={data.navOverview} />
    <NavMenu items={data.navAgentDeveloper} />
    <NavMenu items={data.navToolDeveloper} />
    <NavMenu items={data.navSecurity} />
  </motion.div>
);

function BillingNavItem() {
  const scope = useOrgProjectRouteContext();
  const billingUrl = buildOrgProjectPath("/billing", scope);
  const billing = useSuspenseCapability("billing");
  const { authz } = useSuspenseQuery(
    orgCapabilitiesQueryOptions(scope.organizationId)
  ).data;

  // `billing_read` is org-scoped; allow-all when RBAC is off keeps the pre-RBAC
  // behavior of showing billing to everyone.
  if (!(billing.enabled && hasOrgPermission(authz, ORG.billing.read))) {
    return null;
  }

  return (
    <NavMenuItem
      item={{
        id: "nav-billing",
        title: "Billing",
        url: billingUrl,
        icon: CreditCard,
      }}
    />
  );
}

function BillingNavItemSkeleton() {
  return (
    <SidebarMenuItem>
      <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
        <Skeleton className="size-4" />
        <Skeleton className="h-4 w-24" />
      </div>
    </SidebarMenuItem>
  );
}

function DashboardSidebarFooterLinks() {
  return (
    <SidebarMenu>
      <Suspense fallback={<BillingNavItemSkeleton />}>
        <BillingNavItem />
      </Suspense>
      <SidebarMenuItem>
        <a href="https://docs.arcade.dev" rel="noreferrer" target="_blank">
          <SidebarMenuButton isActive={false} tooltip="Documentation">
            <Book />
            <span>Documentation</span>
          </SidebarMenuButton>
        </a>
      </SidebarMenuItem>
      <CanProject permission={PROJECT.apiKeys.read}>
        <SidebarMenuItem className="my-3">
          <Link
            from="/orgs/$organizationId/projects/$projectId/api-keys"
            preload="intent"
            to="."
          >
            {({ isActive }: { isActive: boolean }) => (
              <SidebarMenuButton
                className={cn(
                  "px-4 py-4 font-semibold transition-all duration-200",
                  isActive
                    ? "bg-foreground text-accent-foreground"
                    : "bg-muted/80 text-foreground ring-1 ring-muted-foreground/25 ring-inset hover:bg-primary hover:text-primary-foreground hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring"
                )}
                isActive={isActive}
                tooltip="Get API Key"
              >
                <Key />
                <span>Get API key</span>
              </SidebarMenuButton>
            )}
          </Link>
        </SidebarMenuItem>
      </CanProject>
    </SidebarMenu>
  );
}

function SidebarCtaSlot() {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { data: eligibility, isPending: isEligibilityPending } = useQuery({
    ...eligibilityQueryOptions(organizationId, projectId),
    // Solo-project eligibility can change when another user accepts an invite.
    refetchInterval: (query) =>
      query.state.data?.active === true ? 60_000 : false,
  });
  const { data: progress, isPending } = useQuery({
    ...onboardingProgressQueryOptions(organizationId, projectId),
    enabled: eligibility?.active === true,
  });

  if (isEligibilityPending || (eligibility?.active === true && isPending)) {
    return (
      <SidebarMenu>
        <SidebarMenuItem className="my-3">
          <Skeleton className="h-10 w-full rounded-md" />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (shouldShowSidebarCta(eligibility, progress)) {
    return <OnboardingCtaButton />;
  }

  return <UpgradeButton />;
}

export const AppSidebar = memo(
  ({ ...props }: React.ComponentProps<typeof Sidebar>) => (
    <Sidebar
      className="overflow-x-hidden"
      collapsible="icon"
      variant="inset"
      {...props}
    >
      <DashboardSidebarHeader />
      <SidebarContent className="scroll-fade overflow-x-hidden">
        <AnimatePresence mode="wait">
          <DashboardMenu />
        </AnimatePresence>
      </SidebarContent>
      <SidebarFooter>
        <DashboardSidebarFooterLinks />
        <SidebarCtaSlot />
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
);
